/**
 * Factor Steps
 *
 * Shared component that renders factor-related steps.
 * Used by all onboarding wizards for the middle part of the flow.
 */

import React, { useEffect, useRef } from 'react';
import { Text } from '@newcore/ui/typography';
import { FactorStepRenderer } from '@/features/onboarding/components/factor-renderer';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { SecondaryFactorsStep } from '@/features/onboarding/components/steps/secondary-factors-step';
import type {
  FactorType,
  OnboardingState,
} from '@/features/onboarding/types/onboarding.types';
import { hasErrorMessage } from '@/features/onboarding/utils/has-error-message';

interface FactorStepsProps {
  state: OnboardingState;
  onSubmitFactor: (payload: Record<string, unknown>) => Promise<boolean>;
  onClearError: () => void;
  onResend?: () => Promise<boolean>;
  canResendSms?: boolean;
  onChooseAlternative: (factorType: FactorType) => Promise<boolean>;
  onBackToAlternatives: () => void;
  onContinue: () => Promise<boolean>;
  isMobileOnboardingEligible?: boolean;
  tenantSlug?: string;
}

/**
 * Factor Steps - renders factor verification/enrollment steps
 *
 * Handles:
 * - Current factor step (OTP, Password, TOTP, WebAuthn, Push)
 * - Alternative selection screen
 * - Auto-continue when canComplete=true but no factors to show
 */
export const FactorSteps: React.FC<FactorStepsProps> = ({
  state,
  onSubmitFactor,
  onClearError,
  onResend,
  canResendSms,
  onChooseAlternative,
  onBackToAlternatives,
  onContinue,
  isMobileOnboardingEligible,
  tenantSlug,
}) => {
  const hasAutoTriggeredRef = useRef(false);

  // Determine if we should auto-continue:
  // canComplete=true, no current factor, no alternatives to show, not loading
  const shouldAutoContinue =
    state.canComplete &&
    !state.currentFactorType &&
    !state.hasAlternative &&
    !state.isLoading;

  // Auto-trigger onContinue when ready to complete but no UI to show
  useEffect(() => {
    if (shouldAutoContinue && !hasAutoTriggeredRef.current) {
      hasAutoTriggeredRef.current = true;
      void onContinue();
    }
  }, [shouldAutoContinue, onContinue]);

  // Reset auto-trigger flag when state changes back to showing UI
  useEffect(() => {
    if (state.currentFactorType || state.hasAlternative) {
      hasAutoTriggeredRef.current = false;
    }
  }, [state.currentFactorType, state.hasAlternative]);

  // Current factor step
  if (state.currentFactorType) {
    const showBackButton = state.hasAlternative;

    return (
      <FactorStepRenderer
        key={state.currentFactorType}
        factorType={state.currentFactorType}
        metadata={state.currentStepMetadata ?? undefined}
        isLoading={state.isLoading}
        error={state.error}
        hasExceededRetries={state.hasExceededRetries}
        onSubmit={onSubmitFactor}
        onClearError={onClearError}
        resendCountdown={state.smsResendCountdown}
        resendCount={state.smsResendCount}
        canResend={canResendSms}
        onResend={onResend}
        showBackButton={showBackButton}
        onBack={onBackToAlternatives}
        isMobileOnboardingEligible={isMobileOnboardingEligible}
        tenantSlug={tenantSlug}
      />
    );
  }

  // Alternative selection screen
  if (state.hasAlternative) {
    return (
      <SecondaryFactorsStep
        alternatives={state.alternatives}
        isLoading={state.isLoading}
        error={state.error}
        canComplete={state.canComplete}
        onContinue={onContinue}
        onClearError={onClearError}
        onChooseAlternative={onChooseAlternative}
      />
    );
  }

  // Terminal error on the auto-continue path: the policy is satisfied, there
  // is no factor UI to render, and ``callCompleteApi`` failed. Without this
  // branch we'd fall through to the loading spinner forever, since the
  // ref-guarded ``useEffect`` won't re-fire ``onContinue`` on its own.
  if (state.error && !state.isLoading) {
    return (
      <div className='space-y-4'>
        {hasErrorMessage(state.error) && (
          <Onboarding.Error error={state.error} onDismiss={onClearError} />
        )}
        <button type='button' onClick={onContinue} className='underline'>
          <Text>Try again</Text>
        </button>
      </div>
    );
  }

  // Fallback loading (brief - will auto-continue if canComplete)
  return <Onboarding.Loading />;
};
